import {combineReducers} from "redux";
import {ProjectLogReducer} from "./projects/ProjectLogReducer";
import { EventNotificationReducer } from "./common/EventNotificationReducer";
import {MainMenuReducer} from "./menu/MainMenuReducer";
import { AuthenticatedUserReducer } from "./common/AuthenticatedUserReducer";
import {NotificationReducer} from "./user/NotificationReducer";
import {UserGroupsReducer} from "./user-groups/UserGroupsReducer";
import {ViewSettingsReducer} from "./common/ViewSettingsReducer";
import { UserGroupsAreasReducer } from "./user-groups/UserGroupsAreasReducer";
import {EmployeesReducer} from "./employees/EmployeesReducer";
import {GeneralSettingsReducer} from "./common/GeneralSettingsReducer";
import { EmployersReducer } from "./employees/EmployersReducer";
import {HelpReducer} from "./common/HelpReducer";
import {TimesheetReducer} from "./timesheet/TimesheetReducer";
import {ProjectReducer} from "./projects/ProjectReducer";
import {SelectComponentsReducer} from "./common/SelectReducer";
import {TimesheetReportReducer} from "./reporting/TimesheetHoursReportReducer";
import {TimesheetTasksReducer} from "./timesheet_tasks/TimesheetTasksReducer";
import {TimesheetTaskPredefDescriptionReducer} from "./timesheet_tasks/TimesheetTaskPredefDescriptionReducer";
import {ProjectRolesReducer} from "./projects/ProjectRolesReducer";
import {ProjectCommissionStateReducer} from "./project-commission-state/ProjectCommissionStateReducer";
import {QualificationReducer} from "./qualifications/QualificationReducer";
import {CustomerReducer} from "./customers/CustomerReducer";
import {CustomerOfficeReducer} from "./customers/CustomerOfficeReducer";
import {CustomerContactReducer} from "./customers/CustomerContactReducer";
import {ExternalWorkReducer} from "./contractors/ExternalWorkReducer";
import {ExternalWorkGroupReducer} from "./contractors/ExternalWorkGroupReducer";
import {ContractorReducer} from "./contractors/ContractorReducer";
import {ContractedWorkReducer} from "./contractors/ContractedWorkReducer";
import {ContractorOfficeReducer} from "./contractors/ContractorOfficeReducer";
import {ContractorContactReducer} from "./contractors/ContractorContactReducer";
import {CalendarReducer} from "./calendar/CalendarReducer";
import {ProjectEmployeeReducer} from "./projects/ProjectEmployeeReducer";
import {ProjectPrivilegesReducer} from "./projects/ProjectPrivilegesReducer";
import {ProjectContractorReducer} from "./projects/ProjectContractorReducer";
import {ProjectPhaseReducer} from "./projects/ProjectPhaseReducer";
import {OutgoingInvoiceReducer} from "./account-book/OutgoingInvoiceReducer";
import {OutgoingInvoiceProjectLogReducer} from "./account-book/OutgoingInvoiceProjectLogReducer";
import { ProjectStagePhaseTaskReducer } from "./projects/ProjectStagePhaseTaskReducer";
import {OutgoingInvoiceProjectTaskTimeItemsReducer} from "./account-book/OutInvoiceProjectTaskTimeItemsReducer";
import {IncomingInvoiceReducer} from "./incoming-invoice/IncomingInvoiceReducer";
import {IncomingInvoicePhaseTaskItemsReducer} from "./incoming-invoice/IncomingInvoicePhaseTaskItemReducer";
import {IncomingInvoiceProjectLogReducer} from "./incoming-invoice/IncomingInvoiceProjectLogReducer";
import {ProjectAppendixReducer} from "./projects/ProjectAppendixReducer";
import {ProjectReportReducer} from "./reporting/ProjectReportReducer";
import {UserAttendanceTimeReducer} from "./timesheet/UserAttendanceTimeReducer";
import {ProjectSolvencyReportReducer} from "./reporting/ProjectSolvencyReportReducer";
import {OutgoingInvoiceTemplateReducer} from "./account-book/OutgoingInvoiceTemplateReducer";
import {AiChatBotReducer} from "./ai/AiChatBotReducer";
import {FormReducer} from "./common/FormReducer";
import {OutgoingInvoiceProjectTaskFlatrateItemsReducer} from "./account-book/OutInvoiceProjectTaskFlatrateItemsReducer";
import {TeamReducer} from "./projects/TeamReducer";
import {ApiKeysReducer} from "./api-keys/ApiKeysReducer";

/**
 * RootReducer is the primary reducer for the application, combining multiple
 * domain-specific reducers into a single state object. Each reducer manages
 * a distinct part of the application state, allowing modular and scalable
 * state management.
 *
 * The combined reducers handle various functionalities, including:
 * - Project management (e.g., projects, employees, roles, phases, tasks)
 * - Notifications and event handling
 * - User and authentication management
 * - Settings and UI preferences
 * - Timesheets and reporting
 * - Contractors, customers, and invoicing
 * - AI-powered chatbot interactions
 * - Form and team management
 *
 * By leveraging `combineReducers`, each sub-reducer is assigned to a specific
 * key in the state object, enabling clear and organized data flow.
 *
 * Structure:
 * - Project-related reducers, such as ProjectReducer, ProjectLogReducer, and
 *   ProjectEmployeeReducer, manage state related to project operations.
 * - User-related reducers, like AuthenticatedUserReducer, UserGroupsReducer,
 *   and UserAttendanceTimeReducer, deal with user data and roles.
 * - NotificationReducer and EventNotificationReducer handle notifications
 *   and events.
 * - Financial reducers, like OutgoingInvoiceReducer and IncomingInvoiceReducer,
 *   process invoicing and financial records.
 * - General reducers, such as GeneralSettingsReducer and ViewSettingsReducer,
 *   maintain configurations and user interface preferences.
 *
 * This reducer is essential for maintaining a consistent, global application state.
 */
export const RootReducer = combineReducers({
    ProjectLogReducer,
    EventNotificationReducer,
    MainMenuReducer,
    AuthenticatedUserReducer,
    NotificationReducer,
    UserGroupsReducer,
    ViewSettingsReducer,
    UserGroupsAreasReducer,
    EmployeesReducer,
    LanguageReducer: GeneralSettingsReducer,
    EmployersReducer,
    HelpReducer,
    TimesheetReducer,
    ProjectReducer,
    SelectComponentsReducer,
    TimesheetReportReducer,
    TimesheetTasksReducer,
    TimesheetTaskPredefDescriptionReducer,
    ProjectRolesReducer,
    ProjectCommissionStateReducer,
    QualificationReducer,
    CustomerReducer,
    CustomerOfficeReducer,
    CustomerContactReducer,
    ExternalWorkReducer,
    ExternalWorkGroupReducer,
    ContractorReducer,
    ContractedWorkReducer,
    ContractorOfficeReducer,
    ContractorContactReducer,
    CalendarReducer,
    ProjectEmployeeReducer,
    ProjectPrivilegesReducer,
    ProjectContractorReducer,
    ProjectAppendixReducer,
    ProjectPhaseReducer,
    OutgoingInvoiceReducer,
    OutgoingInvoiceProjectTaskFlatrateItemsReducer,
    OutgoingInvoiceProjectTaskTimeItemsReducer,
    OutgoingInvoiceProjectLogReducer,
    OutgoingInvoiceTemplateReducer,
    ProjectStagePhaseTaskReducer,
    IncomingInvoiceReducer,
    IncomingInvoicePhaseTaskItemsReducer,
    IncomingInvoiceProjectLogReducer,
    ProjectReportReducer,
    UserAttendanceTimeReducer,
    ProjectSolvencyReportReducer,
    AiChatBotReducer,
    GeneralSettingsReducer,
    FormReducer,
    TeamReducer,
    ApiKeysReducer
});

/**
 * Represents the application state in a Redux store.
 *
 * This type is derived from the root reducer using the `ReturnType` utility type,
 * which infers and represents the structure of the state object.
 *
 * The `AppState` type serves as the single source of truth for the shape of
 * the application's global state, ensuring consistency throughout the app.
 *
 * It is typically used to type the argument for selectors, middleware, or
 * any other functionality that interacts with the global state.
 */
export type AppState = ReturnType< typeof RootReducer >